import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Modal, TextInput, Alert, Pressable, Switch } from 'react-native';
import { User, Appointment } from '../types';
import { hours } from '../constants';
import { styles } from '../styles';
import { TimMessengerHeadless } from '../web-embed/tim-messenger-headless.es.js';

//var api = new TimMessengerHeadless();
//var api = {}

interface CalendarViewProps {
  users: User[];
  onPatientSelect: (user: User) => void;
  appointments: Appointment[];
  onAddAppointment: (appointment: Appointment) => void;
  onMoveAppointment: (appointmentId: number, newDate: Date) => void;
  onUpdateAppointment?: (appointment: Appointment) => void;
}

type ViewMode = 'day' | 'week' | 'month';

const appointmentTitles = [
  'Kontrolluntersuchung',
  'Erstuntersuchung',
  'Blutentnahme',
  'Impfung',
  'Beratung',
  'Nachuntersuchung',
  'Akuter Termin',
  'Überweisung',
  'Laborbesprechung',
  'EKG',
  'Ultraschall',
  'Röntgen',
  'Physiotherapie',
];

const appointmentTypes = [
  'Routine',
  'Dringend',
  'Kontrolltermin',
  'Ersttermin',
  'Notfall',
];

export function CalendarView({ users, onPatientSelect, appointments, onAddAppointment, onMoveAppointment, onUpdateAppointment }: CalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showEditTypeDropdown, setShowEditTypeDropdown] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [dayModalDate, setDayModalDate] = useState<Date | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [originalAppointment, setOriginalAppointment] = useState<Appointment | null>(null);
  const [newAppointment, setNewAppointment] = useState({ title: '', time: '09:00', date: '', patient: '', type: 'Routine', color: '#4CAF50', notifyTIMessenger: true });
  const [draggedAppointment, setDraggedAppointment] = useState<Appointment | null>(null);
  
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const isToday = selectedDateStr === now.toISOString().split('T')[0];
  
  const getAllAppointmentsForSelectedDate = () => {
    return appointments
      .filter(apt => apt.date === selectedDateStr)
      .sort((a, b) => a.time.localeCompare(b.time));
  };
  
  const pastAppointments = getAllAppointmentsForSelectedDate().filter(apt => {
    const aptHour = parseInt(apt.time.split(':')[0]);
    const aptMin = parseInt(apt.time.split(':')[1]);
    return isToday && (aptHour < currentHour || (aptHour === currentHour && aptMin <= currentMinutes));
  });
  
  const futureAppointments = getAllAppointmentsForSelectedDate().filter(apt => {
    const aptHour = parseInt(apt.time.split(':')[0]);
    const aptMin = parseInt(apt.time.split(':')[1]);
    return !isToday || (aptHour > currentHour || (aptHour === currentHour && aptMin > currentMinutes));
  });
  
  const [showAllPast, setShowAllPast] = useState(false);
  const [showAllFuture, setShowAllFuture] = useState(false);
  const [expandedPatients, setExpandedPatients] = useState<string[]>([]);

  const goBack = () => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') newDate.setDate(newDate.getDate() - 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setMonth(newDate.getMonth() - 1);
    setSelectedDate(newDate);
  };
  
  const goForward = () => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') newDate.setDate(newDate.getDate() + 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setMonth(newDate.getMonth() + 1);
    setSelectedDate(newDate);
  };

  const getAppointmentsForHour = (hour: number) => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const filtered = appointments.filter(apt => {
      const aptHour = parseInt(apt.time.split(':')[0]);
      return aptHour === hour && apt.date === selectedDateStr;
    });
    return filtered.sort((a, b) => a.time.localeCompare(b.time));
  };

  const getAppointmentsForDay = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => apt.date === dateStr);
  };

  const findUserByName = (name: string): User | undefined => {
    return users.find(u => u.name === name);
  };

  const handleAppointmentPress = (apt: Appointment) => {
    setEditingAppointment(apt);
    setOriginalAppointment(apt);
    setShowEditModal(true);
  };

  const sendMessageToPatient = async (apt: Appointment, isNew: boolean = false) => {
    if (!apt.notifyTIMessenger) return;
    
    const patient = users.find(u => u.name === apt.patient);
    if (!patient || !patient.mxid) {
      console.log('Keine MXID für Patient gefunden:', apt.patient);
      return;
    }

    const message = isNew
      ? `Ihr neuer Termin "${apt.title}" (${apt.type}) wurde für den ${apt.date} um ${apt.time} erstellt.`
      : `Ihr Termin "${apt.title}" (${apt.type}) am ${apt.date} um ${apt.time} wurde aktualisiert.`;

   const el = document.querySelector('tim-messenger');
   await el.sendDirectMessage({ mxId: patient.mxid, message });

   /*  api.ready().then(function () {
      console.log('Connected! isReady=' + api.isReady);
      //refreshRooms();

      api.sendDirectMessage({ mxId: "lmeier:matrix.auramessenger.de", message: apt.title }).then(function () {
        console.log('DM sent to lmeier:matrix.auramessenger.de' + ': ' + apt.title);
      }).catch(function (err) {
        console.log('ERROR sending DM: ' + err.message);
      });
    }).catch(function (err) {
      
      console.log('ERROR: ' + err.message);
    }); */
   
  }

  const handleSaveEdit = () => {
    if (editingAppointment && onUpdateAppointment) {
      onUpdateAppointment(editingAppointment);
      sendMessageToPatient(editingAppointment, false)
    }
    setShowEditModal(false);
    setEditingAppointment(null);
    setOriginalAppointment(null);
  };

  const hasChanges = () => {
    if (!editingAppointment || !originalAppointment) return false;
    return (
      editingAppointment.title !== originalAppointment.title ||
      editingAppointment.patient !== originalAppointment.patient ||
      editingAppointment.time !== originalAppointment.time ||
      editingAppointment.date !== originalAppointment.date ||
      editingAppointment.type !== originalAppointment.type ||
      editingAppointment.notifyTIMessenger !== originalAppointment.notifyTIMessenger
    );
  };

  const handleDeleteAppointment = () => {
    if (editingAppointment) {
      Alert.alert(
        'Termin löschen',
        'Möchten Sie diesen Termin wirklich löschen?',
        [
          { text: 'Abbrechen', style: 'cancel' },
          { 
            text: 'Löschen', 
            style: 'destructive',
            onPress: () => {
              if (onUpdateAppointment) {
                onUpdateAppointment({ ...editingAppointment, title: '__DELETED__' } as any);
              }
              setShowEditModal(false);
              setEditingAppointment(null);
              setOriginalAppointment(null);
            }
          },
        ]
      );
    }
  };

  const handleAppointmentLongPress = (apt: Appointment) => {
    setDraggedAppointment(apt);
    Alert.alert(
      'Termin verschieben',
      'Wählen Sie den neuen Tag',
      [
        ...dates.map(date => ({
          text: `${date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}`,
          onPress: () => {
            onMoveAppointment(apt.id, date);
            setDraggedAppointment(null);
          }
        })),
        { text: 'Abbrechen', onPress: () => setDraggedAppointment(null), style: 'cancel' }
      ]
    );
  };

  const handleAddAppointment = () => {
    console.log('handleAddAppointment called', newAppointment);
    if (!newAppointment.title || !newAppointment.patient) {
      Alert.alert('Fehler', 'Bitte geben Sie einen Titel und einen Patienten ein.');
      return;
    }
    const apt: Appointment = {
      id: Date.now(),
      time: newAppointment.time,
      date: newAppointment.date || selectedDate.toISOString().split('T')[0],
      title: newAppointment.title,
      patient: newAppointment.patient,
      type: newAppointment.type,
      color: newAppointment.color,
      notifyTIMessenger: newAppointment.notifyTIMessenger,
    };
    console.log('Calling onAddAppointment', apt);
    onAddAppointment(apt);
    sendMessageToPatient(apt, true);
    console.log('Closing modal');
    setShowAddModal(false);
    setNewAppointment({ title: '', time: '09:00', date: '', patient: '', type: 'Routine', color: '#4CAF50', notifyTIMessenger: false });
  };

  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const getWeekDates = () => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(d);
      date.setDate(diff + i);
      return date;
    });
  };
  const dates = getWeekDates();

  const getMonthDates = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const days: (Date | null)[] = [];
    for (let i = 0; i < startPadding; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const formatDateHeader = () => {
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = dates[0];
      const end = dates[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString('de-DE', { month: 'long' })} ${start.getFullYear()}`;
      }
      return `${start.toLocaleDateString('de-DE', { month: 'short' })} - ${end.toLocaleDateString('de-DE', { month: 'short' })} ${end.getFullYear()}`;
    }
    return selectedDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  };

  const renderNavigation = () => (
    <View style={styles.navRow}>
      <View style={styles.navArrows}>
        <TouchableOpacity onPress={goBack} style={styles.navArrow}>
          <Text style={styles.navArrowText}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.navDate}>{formatDateHeader()}</Text>
        <TouchableOpacity onPress={goForward} style={styles.navArrow}>
          <Text style={styles.navArrowText}>›</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.navButtonsRow}>
        <View style={styles.viewModeButtons}>
          <TouchableOpacity style={[styles.viewModeBtn, viewMode === 'day' && styles.viewModeBtnActive]} onPress={() => setViewMode('day')}>
            <Text style={[styles.viewModeBtnText, viewMode === 'day' && styles.viewModeBtnTextActive]}>Tag</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.viewModeBtn, viewMode === 'week' && styles.viewModeBtnActive]} onPress={() => setViewMode('week')}>
            <Text style={[styles.viewModeBtnText, viewMode === 'week' && styles.viewModeBtnTextActive]}>Woche</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.viewModeBtn, viewMode === 'month' && styles.viewModeBtnActive]} onPress={() => setViewMode('month')}>
            <Text style={[styles.viewModeBtnText, viewMode === 'month' && styles.viewModeBtnTextActive]}>Monat</Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity style={styles.addButton} onPress={() => {
          const today = selectedDate.toISOString().split('T')[0];
          setNewAppointment({ 
            ...newAppointment, 
            date: today 
          });
          setShowAddModal(true);
        }}>
          <Text style={styles.addButtonText}>+ Termin</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderWeekView = () => {
    const allAppointments = getAllAppointmentsForSelectedDate();

    const appointmentsByPatient = allAppointments.reduce((acc, apt) => {
      if (!acc[apt.patient]) acc[apt.patient] = [];
      acc[apt.patient].push(apt);
      return acc;
    }, {} as Record<string, Appointment[]>);

    const patients = Object.keys(appointmentsByPatient).sort();

    return (
      <>
        <View style={styles.weekRow}>
          {dates.map((date, i) => {
            const dateStr = date.toISOString().split('T')[0];
            const isToday = dateStr === now.toISOString().split('T')[0];
            return (
              <TouchableOpacity
                key={i}
                style={[
                  styles.weekDay,
                  isToday && styles.weekDayToday,
                  dateStr === selectedDateStr && styles.weekDaySelected
                ]}
                onPress={() => setSelectedDate(date)}
              >
                <Text style={styles.weekDayName}>{weekDays[i]}</Text>
                <Text style={[styles.weekDayNumber, isToday && styles.weekDayNumberToday]}>{date.getDate()}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {patients.length > 0 ? (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.patientColumnsContainer}>
            {patients.map(patient => {
              const patientApts = appointmentsByPatient[patient].sort((a, b) => a.time.localeCompare(b.time));
              const isPast = (apt: Appointment) => {
                const aptHour = parseInt(apt.time.split(':')[0]);
                const aptMin = parseInt(apt.time.split(':')[1]);
                return isToday && (aptHour < currentHour || (aptHour === currentHour && aptMin <= currentMinutes));
              };
              const pastApts = patientApts.filter(isPast);
              const futureApts = patientApts.filter(apt => !isPast(apt));
              const isExpanded = expandedPatients.includes(patient);

              return (
                <View key={patient} style={styles.patientColumn}>
                  <View style={[styles.patientColumnHeader, { backgroundColor: getPatientColor(patient) }]}>
                    <Text style={styles.patientColumnAvatar}>{patient.charAt(0)}</Text>
                    <Text style={styles.patientColumnName} numberOfLines={1}>{patient}</Text>
                  </View>
                  <ScrollView style={styles.patientColumnContent}>
                    {pastApts.length > 0 && !isExpanded && isToday && (
                      <TouchableOpacity
                        style={styles.pastAppointmentsCollapsed}
                        onPress={() => setExpandedPatients([...expandedPatients, patient])}
                      >
                        <Text style={styles.pastAppointmentsCollapsedText}>
                          {pastApts.length} vergangen{pastApts.length !== 1 ? 'e' : ''}
                        </Text>
                      </TouchableOpacity>
                    )}
                    {pastApts.length > 0 && isExpanded && (
                      <>
                        <TouchableOpacity
                          style={styles.showMoreButton}
                          onPress={() => setExpandedPatients(expandedPatients.filter(p => p !== patient))}
                        >
                          <Text style={styles.showMoreText}>Vergangen einklappen</Text>
                        </TouchableOpacity>
                        {pastApts.map(apt => renderAppointmentItem(apt, true))}
                      </>
                    )}
                    {isToday && pastApts.length > 0 && futureApts.length > 0 && (
                      <View style={styles.timelineNow}>
                        <View style={styles.timelineNowLine} />
                        <Text style={styles.timelineNowText}>JETZT</Text>
                      </View>
                    )}
                    {futureApts.map(apt => renderAppointmentItem(apt, false))}
                  </ScrollView>
                </View>
              );
            })}
          </ScrollView>
        ) : (
          <View style={styles.timelineEmpty}>
            <Text style={styles.timelineEmptyText}>Keine Termine an diesem Tag</Text>
          </View>
        )}
      </>
    );
  };
  
  const getPatientColor = (patientName: string) => {
    const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#8BC34A', '#FF5722', '#673AB7', '#009688'];
    let hash = 0;
    for (let i = 0; i < patientName.length; i++) {
      hash = patientName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const renderAppointmentItem = (apt: Appointment, isPast: boolean = false) => (
    <TouchableOpacity 
      key={apt.id}
      style={[styles.appointmentCard, isPast && styles.appointmentCardPast, { borderLeftColor: apt.color || getPatientColor(apt.patient) }]} 
      onPress={() => handleAppointmentPress(apt)}
      onLongPress={() => handleAppointmentLongPress(apt)}
    >
      <View style={styles.appointmentHeader}>
        <View style={[styles.appointmentAvatar, { backgroundColor: getPatientColor(apt.patient) }]}>
          <Text style={styles.appointmentAvatarText}>{apt.patient.charAt(0)}</Text>
        </View>
        <View style={styles.appointmentHeaderInfo}>
          <Text style={[styles.appointmentTime, isPast && styles.appointmentTimePast]}>{apt.time}</Text>
          <Text style={[styles.appointmentPatient, isPast && styles.appointmentPatientPast]}>{apt.patient}</Text>
        </View>
        <View style={styles.appointmentHeaderRight}>
          {apt.notifyTIMessenger && (
            <Text style={styles.appointmentTIMBadge}>TIM 🔔</Text>
          )}
        </View>
      </View>
      <Text style={[styles.appointmentTitle, isPast && styles.appointmentTitlePast]}>{apt.title}</Text>
      <Text style={styles.appointmentType}>{apt.type}</Text>
      {apt.date !== now.toISOString().split('T')[0] && (
        <Text style={styles.appointmentDate}>{apt.date}</Text>
      )}
    </TouchableOpacity>
  );

  const renderDayView = () => {
    const allAppointments = getAllAppointmentsForSelectedDate();
    const displayPast = showAllPast ? pastAppointments : pastAppointments.slice(-2);
    const displayFuture = showAllFuture ? futureAppointments : futureAppointments.slice(0, 8);
    
    return (
      <ScrollView style={styles.timelineContainer}>
        {pastAppointments.length > 0 && (
          <View style={styles.timelineSection}>
            {!showAllPast && pastAppointments.length > 2 && (
              <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllPast(true)}>
                <Text style={styles.showMoreText}>+ {pastAppointments.length} vergangene</Text>
              </TouchableOpacity>
            )}
            {showAllPast && (
              <>
                <Text style={styles.timelineSectionTitle}>Vergangen</Text>
                {displayPast.map(apt => renderAppointmentItem(apt, true))}
                {pastAppointments.length > 2 && (
                  <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllPast(false)}>
                    <Text style={styles.showMoreText}>Weniger anzeigen</Text>
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        )}
        
        {isToday && (
          <View style={styles.timelineNow}>
            <View style={styles.timelineNowLine} />
            <Text style={styles.timelineNowText}>JETZT</Text>
            <Text style={styles.timelineNowTime}>{currentHour.toString().padStart(2, '0')}:{currentMinutes.toString().padStart(2, '0')}</Text>
          </View>
        )}
        
        {pastAppointments.length > 0 && futureAppointments.length === 0 && isToday && (
          <View style={styles.timelineEmpty}>
            <Text style={styles.timelineEmptyText}>Keine weiteren Termine heute</Text>
          </View>
        )}
        
        {futureAppointments.length > 0 && (
          <View style={styles.timelineSection}>
            {!isToday && pastAppointments.length === 0 && <Text style={styles.timelineSectionTitle}>Termine</Text>}
            <Text style={styles.timelineSectionTitle}>{isToday ? 'Kommend' : 'Termine'}</Text>
            {displayFuture.map(apt => renderAppointmentItem(apt))}
            {!showAllFuture && futureAppointments.length > 8 && (
              <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllFuture(true)}>
                <Text style={styles.showMoreText}>+ {futureAppointments.length - 8} mehr</Text>
              </TouchableOpacity>
            )}
            {showAllFuture && futureAppointments.length > 8 && (
              <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllFuture(false)}>
                <Text style={styles.showMoreText}>Weniger anzeigen</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        
        {allAppointments.length === 0 && (
          <View style={styles.timelineEmpty}>
            <Text style={styles.timelineEmptyText}>Keine Termine an diesem Tag</Text>
          </View>
        )}
      </ScrollView>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates();
    
    const handleDayPress = (date: Date) => {
      setSelectedDate(date);
      setDayModalDate(date);
      setShowDayModal(true);
    };
    
    const allAppointments = getAllAppointmentsForSelectedDate();
    const displayPast = showAllPast ? pastAppointments : pastAppointments.slice(-2);
    const displayFuture = showAllFuture ? futureAppointments : futureAppointments.slice(0, 8);
    
    return (
      <ScrollView style={styles.monthViewContainer}>
        <View style={styles.monthGrid}>
          {weekDays.map(day => <Text key={day} style={styles.monthWeekday}>{day}</Text>)}
          {monthDates.map((date, i) => {
            const dateStr = date ? date.toISOString().split('T')[0] : '';
            const isToday = dateStr === now.toISOString().split('T')[0];
            const isSelected = dateStr === selectedDateStr;
            const allDayAppts = date ? getAppointmentsForDay(date).sort((a, b) => a.time.localeCompare(b.time)) : [];
            
            let pastAppts: Appointment[] = [];
            let futureAppts: Appointment[] = [];
            
            if (isToday) {
              const currentTimeMinutes = currentHour * 60 + currentMinutes;
              pastAppts = allDayAppts.filter(apt => {
                const aptMinutes = parseInt(apt.time.split(':')[0]) * 60 + parseInt(apt.time.split(':')[1]);
                return aptMinutes <= currentTimeMinutes;
              });
              futureAppts = allDayAppts.filter(apt => {
                const aptMinutes = parseInt(apt.time.split(':')[0]) * 60 + parseInt(apt.time.split(':')[1]);
                return aptMinutes > currentTimeMinutes;
              });
            } else {
              futureAppts = allDayAppts;
            }
            
            const hasMore = allDayAppts.length > 6;
            const displayPast = pastAppts.slice(0, 2);
            const displayFuture = futureAppts.slice(0, hasMore ? 4 : 6);
            
            return (
              <TouchableOpacity 
                key={i} 
                style={[styles.monthDay, isToday && styles.monthDayToday, isSelected && styles.monthDaySelected, styles.monthDayBorder]} 
                onPress={() => date && handleDayPress(date)} 
                disabled={!date}
              >
                <View style={styles.monthDayHeader}>
                  <Text style={[styles.monthDayText, isToday && styles.monthDayTextToday]}>{date ? date.getDate() : ''}</Text>
                </View>
                {allDayAppts.length > 0 ? (
                  <View style={styles.monthAppointmentsInDay}>
                    {hasMore && (
                      <TouchableOpacity 
                        style={styles.monthMoreTouch}
                        onPress={() => handleDayPress(date!)}
                      >
                        <Text style={styles.monthMoreText}>+{allDayAppts.length} Termine</Text>
                      </TouchableOpacity>
                    )}
                    {isToday && pastAppts.length > 0 && displayPast.map((apt, j) => (
                      <View key={`past-${j}`} style={[styles.monthAppointmentBar, styles.monthAppointmentPast, { backgroundColor: apt.color }]}>
                        <Text style={styles.monthAppointmentBarText} numberOfLines={1}>
                          {apt.time.slice(0, 5)} {apt.title}
                        </Text>
                      </View>
                    ))}
                    {displayFuture.map((apt, j) => (
                      <TouchableOpacity 
                        key={`future-${j}`}
                        style={[styles.monthAppointmentBar, { backgroundColor: apt.color }]}
                        onPress={() => {
                          setEditingAppointment(apt);
                          setShowEditModal(true);
                        }}
                      >
                        <Text style={styles.monthAppointmentBarText} numberOfLines={1}>
                          {apt.time.slice(0, 5)} {apt.title}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : (
                  <View style={styles.monthAppointmentsInDay} />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
        
        <View style={styles.timelineContainer}>
          {pastAppointments.length > 0 && (
            <View style={styles.timelineSection}>
              <Text style={styles.timelineSectionTitle}>Vergangen</Text>
              {displayPast.map(apt => renderAppointmentItem(apt))}
              {!showAllPast && pastAppointments.length > 2 && (
                <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllPast(true)}>
                  <Text style={styles.showMoreText}>+ {pastAppointments.length - 2} mehr</Text>
                </TouchableOpacity>
              )}
              {showAllPast && pastAppointments.length > 2 && (
                <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllPast(false)}>
                  <Text style={styles.showMoreText}>Weniger anzeigen</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          
          {isToday && (
            <View style={styles.timelineNow}>
              <View style={styles.timelineNowLine} />
              <Text style={styles.timelineNowText}>JETZT</Text>
              <Text style={styles.timelineNowTime}>{currentHour.toString().padStart(2, '0')}:{now.getMinutes().toString().padStart(2, '0')}</Text>
            </View>
          )}
          
          {pastAppointments.length > 0 && futureAppointments.length === 0 && isToday && (
            <View style={styles.timelineEmpty}>
              <Text style={styles.timelineEmptyText}>Keine weiteren Termine heute</Text>
            </View>
          )}
          
          {futureAppointments.length > 0 && (
            <View style={styles.timelineSection}>
              {!isToday && pastAppointments.length === 0 && <Text style={styles.timelineSectionTitle}>Termine</Text>}
              <Text style={styles.timelineSectionTitle}>{isToday ? 'Kommend' : 'Termine'}</Text>
              {displayFuture.map(apt => renderAppointmentItem(apt))}
              {!showAllFuture && futureAppointments.length > 8 && (
                <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllFuture(true)}>
                  <Text style={styles.showMoreText}>+ {futureAppointments.length - 8} mehr</Text>
                </TouchableOpacity>
              )}
              {showAllFuture && futureAppointments.length > 8 && (
                <TouchableOpacity style={styles.showMoreButton} onPress={() => setShowAllFuture(false)}>
                  <Text style={styles.showMoreText}>Weniger anzeigen</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          
          {allAppointments.length === 0 && (
            <View style={styles.timelineEmpty}>
              <Text style={styles.timelineEmptyText}>Keine Termine an diesem Tag</Text>
            </View>
          )}
        </View>
      </ScrollView>
    );
  };

  return (
    <ScrollView style={styles.contentContainer}>
      <Text style={styles.contentTitle}>Terminkalender</Text>
      {renderNavigation()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}
      {viewMode === 'month' && renderMonthView()}

      <Modal visible={showAddModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Neuer Termin</Text>
            
            <Text style={styles.modalLabel}>Titel</Text>
            <TouchableOpacity 
              style={styles.modalInput} 
              onPress={() => setShowTitleDropdown(true)}
            >
              <Text style={newAppointment.title ? styles.dropdownText : styles.dropdownPlaceholder}>
                {newAppointment.title || "Titel auswählen"}
              </Text>
            </TouchableOpacity>

            <Text style={styles.modalLabel}>Patient</Text>
            <TouchableOpacity 
              style={styles.modalInput} 
              onPress={() => setShowPatientDropdown(true)}
            >
              <Text style={newAppointment.patient ? styles.dropdownText : styles.dropdownPlaceholder}>
                {newAppointment.patient || "Patient auswählen"}
              </Text>
            </TouchableOpacity>

            <Text style={styles.modalLabel}>Uhrzeit</Text>
            <TextInput
              style={styles.modalInput}
              value={newAppointment.time}
              onChangeText={(text) => setNewAppointment({ ...newAppointment, time: text })}
              placeholder="09:00"
            />

            <Text style={styles.modalLabel}>Datum</Text>
            <TextInput
              style={styles.modalInput}
              value={newAppointment.date}
              onChangeText={(text) => setNewAppointment({ ...newAppointment, date: text })}
              placeholder="2024-03-15"
            />

            <Text style={styles.modalLabel}>Typ</Text>
            <TouchableOpacity 
              style={styles.modalInput} 
              onPress={() => setShowTypeDropdown(true)}
            >
              <Text style={newAppointment.type ? styles.dropdownText : styles.dropdownPlaceholder}>
                {newAppointment.type || "Typ auswählen"}
              </Text>
            </TouchableOpacity>

            <View style={styles.checkboxRow}>
              <Switch
                value={newAppointment.notifyTIMessenger}
                onValueChange={(value) => setNewAppointment({ ...newAppointment, notifyTIMessenger: value })}
                trackColor={{ false: '#e0e0e0', true: '#4CAF50' }}
                thumbColor="#fff"
              />
              <Text style={styles.checkboxLabel}>Über TI-Messenger informieren</Text>
            </View>

            <View style={styles.modalButtons}>
              <Pressable style={styles.modalCancelButton} onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalCancelText}>Abbrechen</Text>
              </Pressable>
              <Pressable style={styles.modalSaveButton} onPress={handleAddAppointment}>
                <Text style={styles.modalSaveText}>Erstellen</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showPatientDropdown} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Patient auswählen</Text>
            <ScrollView style={styles.dropdownList}>
              {users.map(user => (
                <TouchableOpacity 
                  key={user.id} 
                  style={styles.dropdownItem}
                  onPress={() => {
                    setNewAppointment({ ...newAppointment, patient: user.name });
                    setShowPatientDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownItemText}>{user.name}</Text>
                  <Text style={styles.dropdownItemSubtext}>{user.insurance}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Pressable style={styles.modalCancelButton} onPress={() => setShowPatientDropdown(false)}>
              <Text style={styles.modalCancelText}>Abbrechen</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal visible={showTitleDropdown} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Titel auswählen</Text>
            <ScrollView style={styles.dropdownList}>
              {appointmentTitles.map((title, index) => (
                <TouchableOpacity 
                  key={index} 
                  style={styles.dropdownItem}
                  onPress={() => {
                    setNewAppointment({ ...newAppointment, title });
                    setShowTitleDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownItemText}>{title}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Pressable style={styles.modalCancelButton} onPress={() => setShowTitleDropdown(false)}>
              <Text style={styles.modalCancelText}>Abbrechen</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal visible={showTypeDropdown} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Typ auswählen</Text>
            <ScrollView style={styles.dropdownList}>
              {appointmentTypes.map((type, index) => (
                <TouchableOpacity 
                  key={index} 
                  style={styles.dropdownItem}
                  onPress={() => {
                    setNewAppointment({ ...newAppointment, type });
                    setShowTypeDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Pressable style={styles.modalCancelButton} onPress={() => setShowTypeDropdown(false)}>
              <Text style={styles.modalCancelText}>Abbrechen</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal visible={showEditTypeDropdown} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Typ auswählen</Text>
            <ScrollView style={styles.dropdownList}>
              {appointmentTypes.map((type, index) => (
                <TouchableOpacity 
                  key={index} 
                  style={styles.dropdownItem}
                  onPress={() => {
                    setEditingAppointment(editingAppointment ? { ...editingAppointment, type } : null);
                    setShowEditTypeDropdown(false);
                    setTimeout(() => setShowEditModal(true), 300);
                  }}
                >
                  <Text style={styles.dropdownItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Pressable style={styles.modalCancelButton} onPress={() => {
              setShowEditTypeDropdown(false);
              setTimeout(() => setShowEditModal(true), 300);
            }}>
              <Text style={styles.modalCancelText}>Abbrechen</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal visible={showEditModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Termin bearbeiten</Text>
            
            <Text style={styles.modalLabel}>Titel</Text>
            <TextInput
              style={styles.modalInput}
              value={editingAppointment?.title || ''}
              onChangeText={(text) => setEditingAppointment(editingAppointment ? { ...editingAppointment, title: text } : null)}
              placeholder="z.B. Kontrolluntersuchung"
            />

            <Text style={styles.modalLabel}>Patient</Text>
            <TextInput
              style={styles.modalInput}
              value={editingAppointment?.patient || ''}
              onChangeText={(text) => setEditingAppointment(editingAppointment ? { ...editingAppointment, patient: text } : null)}
              placeholder="Patientenname"
            />

            <Text style={styles.modalLabel}>Uhrzeit</Text>
            <TextInput
              style={styles.modalInput}
              value={editingAppointment?.time || ''}
              onChangeText={(text) => setEditingAppointment(editingAppointment ? { ...editingAppointment, time: text } : null)}
              placeholder="09:00"
            />

            <Text style={styles.modalLabel}>Datum</Text>
            <TextInput
              style={styles.modalInput}
              value={editingAppointment?.date || ''}
              onChangeText={(text) => setEditingAppointment(editingAppointment ? { ...editingAppointment, date: text } : null)}
              placeholder="2024-03-15"
            />

            <Text style={styles.modalLabel}>Typ</Text>
            <TouchableOpacity 
              style={styles.modalInput}
              onPress={() => {
                setShowEditModal(false);
                setTimeout(() => setShowEditTypeDropdown(true), 300);
              }}
            >
              <Text style={editingAppointment?.type ? styles.dropdownText : styles.dropdownPlaceholder}>
                {editingAppointment?.type || "Typ auswählen"}
              </Text>
            </TouchableOpacity>

            <View style={styles.checkboxRow}>
              <Switch
                value={editingAppointment?.notifyTIMessenger || false}
                onValueChange={(value) => setEditingAppointment(editingAppointment ? { ...editingAppointment, notifyTIMessenger: value } : null)}
                trackColor={{ false: '#e0e0e0', true: '#4CAF50' }}
                thumbColor="#fff"
              />
              <Text style={styles.checkboxLabel}>Über TI-Messenger informieren</Text>
            </View>

            <View style={styles.modalButtons}>
              <Pressable style={styles.modalDeleteButton} onPress={handleDeleteAppointment}>
                <Text style={styles.modalDeleteText}>Löschen</Text>
              </Pressable>
              <Pressable style={styles.modalCancelButton} onPress={() => { setShowEditModal(false); setEditingAppointment(null); setOriginalAppointment(null); }}>
                <Text style={styles.modalCancelText}>Abbrechen</Text>
              </Pressable>
              <Pressable style={[styles.modalSaveButton, !hasChanges() && styles.modalSaveButtonDisabled]} onPress={handleSaveEdit} disabled={!hasChanges()}>
                <Text style={[styles.modalSaveText, !hasChanges() && styles.modalSaveTextDisabled]}>Speichern</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showDayModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.dayModalContent]}>
            <View style={styles.dayModalHeader}>
              <Text style={styles.dayModalTitle}>
                {dayModalDate?.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </Text>
              <Pressable onPress={() => setShowDayModal(false)}>
                <Text style={styles.dayModalClose}>✕</Text>
              </Pressable>
            </View>
            <ScrollView style={styles.dayModalList}>
              {dayModalDate ? getAppointmentsForDay(dayModalDate).sort((a, b) => a.time.localeCompare(b.time)).map(apt => (
                <TouchableOpacity 
                  key={apt.id}
                  style={[styles.dayModalItem, { borderLeftColor: apt.color }]}
                  onPress={() => {
                    setEditingAppointment(apt);
                    setShowDayModal(false);
                    setShowEditModal(true);
                  }}
                >
                  <Text style={styles.dayModalItemTime}>{apt.time}</Text>
                  <View style={styles.dayModalItemContent}>
                    <Text style={styles.dayModalItemTitle}>{apt.title}</Text>
                    <Text style={styles.dayModalItemPatient}>{apt.patient}</Text>
                    <Text style={styles.dayModalItemType}>{apt.type}</Text>
                  </View>
                </TouchableOpacity>
              )) : null}
              {dayModalDate && getAppointmentsForDay(dayModalDate).length === 0 && (
                <Text style={styles.dayModalEmpty}>Keine Termine an diesem Tag</Text>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}
